/**
 * CRM Form Dialogs — create / edit dialogs for companies, contacts, deals, interactions.
 *
 * Uses shared Dialog, Input, Select, Textarea from ui.tsx.
 */

import React, { useState, useEffect } from 'react';
import { Dialog, Input, Select, Textarea, Button, Spinner, toast } from '../../components/ui';
import {
  createCompany,
  updateCompany,
  createContact,
  updateContact,
  createDeal,
  updateDeal,
  createInteraction,
  createFollowup,
  fetchCompanies,
  fetchContacts,
} from '../../lib/crm-api';
import type { CrmCompany, CrmContact, CrmDeal } from '../../lib/crm-api';
import { useT } from '../../lib/i18n';

// ─── Company Form ────────────────────────────────────────

interface CompanyFormProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  initial?: CrmCompany | null;
}

const EMPTY_COMPANY = {
  name: '',
  domain: '',
  country: '',
  industry: '',
  company_size: '',
  website: '',
  phone: '',
  address: '',
  notes: '',
};

export function CompanyFormDialog({ open, onClose, onSaved, initial }: CompanyFormProps) {
  const t = useT();
  const [form, setForm] = useState(EMPTY_COMPANY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(
        initial
          ? {
              name: initial.name,
              domain: initial.domain || '',
              country: initial.country || '',
              industry: initial.industry || '',
              company_size: initial.company_size || '',
              website: initial.website || '',
              phone: initial.phone || '',
              address: initial.address || '',
              notes: initial.notes || '',
            }
          : EMPTY_COMPANY,
      );
    }
  }, [open, initial]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast(t('crm.companyNameRequired'), 'error');
      return;
    }
    setSaving(true);
    try {
      if (initial) {
        await updateCompany(initial.id, form);
        toast(t('crm.companyUpdated'), 'success');
      } else {
        await createCompany({ ...form, source: 'manual' });
        toast(t('crm.companyCreated'), 'success');
      }
      onSaved();
      onClose();
    } catch (err: unknown) {
      toast((err as Error).message || t('crm.saveFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const set = (k: string, v: string) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <Dialog open={open} onClose={onClose} title={initial ? t('crm.editCompanyTitle') : t('crm.newCompany')} size="lg">
      <div className="space-y-4">
        <div>
          <label className="text-xs text-fg-muted mb-1 block">Name *</label>
          <Input
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder={t('crm.companyNamePlaceholder')}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-fg-muted mb-1 block">Domain</label>
            <Input
              value={form.domain}
              onChange={(e) => set('domain', e.target.value)}
              placeholder={t('crm.domainPlaceholder')}
            />
          </div>
          <div>
            <label className="text-xs text-fg-muted mb-1 block">Website</label>
            <Input value={form.website} onChange={(e) => set('website', e.target.value)} placeholder="https://..." />
          </div>
          <div>
            <label className="text-xs text-fg-muted mb-1 block">Country</label>
            <Input
              value={form.country}
              onChange={(e) => set('country', e.target.value)}
              placeholder={t('crm.countryPlaceholder')}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-fg-muted mb-1 block">Industry</label>
            <Input
              value={form.industry}
              onChange={(e) => set('industry', e.target.value)}
              placeholder={t('crm.industryPlaceholder')}
            />
          </div>
          <div>
            <label className="text-xs text-fg-muted mb-1 block">Phone</label>
            <Input
              value={form.phone}
              onChange={(e) => set('phone', e.target.value)}
              placeholder={t('crm.phonePlaceholder')}
            />
          </div>
          <div>
            <label className="text-xs text-fg-muted mb-1 block">Size</label>
            <Select value={form.company_size} onChange={(e) => set('company_size', e.target.value)}>
              <option value="">—</option>
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
              <option value="enterprise">Enterprise</option>
            </Select>
          </div>
        </div>
        <div>
          <label className="text-xs text-fg-muted mb-1 block">Address</label>
          <Input
            value={form.address}
            onChange={(e) => set('address', e.target.value)}
            placeholder={t('crm.addressPlaceholder')}
          />
        </div>
        <div>
          <label className="text-xs text-fg-muted mb-1 block">Notes</label>
          <Textarea
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
            rows={4}
            placeholder={t('crm.notesPlaceholder')}
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Spinner className="h-3 w-3 mr-1" />}
            {initial ? 'Save' : 'Create'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ─── Contact Form ────────────────────────────────────────

interface ContactFormProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  initial?: CrmContact | null;
  defaultCompanyId?: number;
}

const EMPTY_CONTACT = {
  name: '',
  email: '',
  phone: '',
  title: '',
  department: '',
  timezone: '',
  language: '',
  notes: '',
  company_id: '' as string,
};

export function ContactFormDialog({ open, onClose, onSaved, initial, defaultCompanyId }: ContactFormProps) {
  const t = useT();
  const [form, setForm] = useState(EMPTY_CONTACT);
  const [companies, setCompanies] = useState<CrmCompany[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(
        initial
          ? {
              name: initial.name,
              email: initial.email || '',
              phone: initial.phone || '',
              title: initial.title || '',
              department: initial.department || '',
              timezone: initial.timezone || '',
              language: initial.language || '',
              notes: initial.notes || '',
              company_id: String(initial.company_id || ''),
            }
          : { ...EMPTY_CONTACT, company_id: String(defaultCompanyId || '') },
      );
      fetchCompanies({ limit: 200 })
        .then((r) => setCompanies(r.items))
        .catch(() => {});
    }
  }, [open, initial, defaultCompanyId]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast(t('crm.nameRequired'), 'error');
      return;
    }
    if (!form.email.trim()) {
      toast(t('crm.emailRequired'), 'error');
      return;
    }
    setSaving(true);
    try {
      const data = { ...form, company_id: form.company_id ? Number(form.company_id) : null };
      if (initial) {
        await updateContact(initial.id, data);
        toast(t('crm.contactUpdated'), 'success');
      } else {
        await createContact({ ...data, source: 'manual' });
        toast(t('crm.contactCreated'), 'success');
      }
      onSaved();
      onClose();
    } catch (err: unknown) {
      toast((err as Error).message || t('crm.saveFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const set = (k: string, v: string) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <Dialog open={open} onClose={onClose} title={initial ? t('crm.editContactTitle') : t('crm.newContact')} size="lg">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-fg-muted mb-1 block">Name *</label>
            <Input
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder={t('crm.fullNamePlaceholder')}
            />
          </div>
          <div>
            <label className="text-xs text-fg-muted mb-1 block">Email *</label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              placeholder={t('crm.emailPlaceholder')}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-fg-muted mb-1 block">Job Title</label>
            <Input
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder={t('crm.jobTitlePlaceholder')}
            />
          </div>
          <div>
            <label className="text-xs text-fg-muted mb-1 block">Company</label>
            <Select value={form.company_id} onChange={(e) => set('company_id', e.target.value)}>
              <option value="">— No Company —</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-fg-muted mb-1 block">Phone</label>
            <Input
              value={form.phone}
              onChange={(e) => set('phone', e.target.value)}
              placeholder={t('crm.phonePlaceholder')}
            />
          </div>
          <div>
            <label className="text-xs text-fg-muted mb-1 block">Department</label>
            <Input
              value={form.department}
              onChange={(e) => set('department', e.target.value)}
              placeholder={t('crm.departmentPlaceholder')}
            />
          </div>
        </div>
        <div>
          <label className="text-xs text-fg-muted mb-1 block">Notes</label>
          <Textarea
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
            rows={2}
            placeholder={t('crm.notesPlaceholder')}
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Spinner className="h-3 w-3 mr-1" />}
            {initial ? 'Save' : 'Create'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ─── Interaction Form ────────────────────────────────────

interface InteractionFormProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  contactId: number;
  companyId?: number | null;
}

export function InteractionFormDialog({ open, onClose, onSaved, contactId, companyId }: InteractionFormProps) {
  const t = useT();
  const [form, setForm] = useState({ type: 'note', subject: '', content: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ type: 'note', subject: '', content: '' });
  }, [open]);

  const handleSave = async () => {
    if (!form.content.trim()) {
      toast(t('crm.contentRequired'), 'error');
      return;
    }
    setSaving(true);
    try {
      await createInteraction({
        contact_id: contactId,
        company_id: companyId || undefined,
        type: form.type,
        subject: form.subject || undefined,
        content: form.content,
      } as never);
      toast(t('crm.interactionLogged'), 'success');
      onSaved();
      onClose();
    } catch (err: unknown) {
      toast((err as Error).message || t('crm.saveFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title={t('crm.logInteraction')} size="lg">
      <div className="space-y-3">
        <div>
          <label className="text-xs text-fg-muted mb-1 block">Type</label>
          <Select value={form.type} onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))}>
            <option value="note">Note</option>
            <option value="call">Call</option>
            <option value="meeting">Meeting</option>
            <option value="email_outbound">Email (Outbound)</option>
          </Select>
        </div>
        <div>
          <label className="text-xs text-fg-muted mb-1 block">Subject</label>
          <Input
            value={form.subject}
            onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))}
            placeholder="Optional subject"
          />
        </div>
        <div>
          <label className="text-xs text-fg-muted mb-1 block">Content *</label>
          <Textarea
            value={form.content}
            onChange={(e) => setForm((p) => ({ ...p, content: e.target.value }))}
            rows={4}
            placeholder="Details..."
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Spinner className="h-3 w-3 mr-1" />}
            Save
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ─── Deal Form ───────────────────────────────────────────

interface DealFormProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  initial?: CrmDeal | null;
  defaultCompanyId?: number;
  defaultContactId?: number;
}

const EMPTY_DEAL = {
  title: '',
  type: 'quotation',
  currency: 'USD',
  payment_terms: '',
  shipping_terms: '',
  notes: '',
  company_id: '' as string,
  contact_id: '' as string,
  expected_close_date: '',
};

export function DealFormDialog({ open, onClose, onSaved, initial, defaultCompanyId, defaultContactId }: DealFormProps) {
  const t = useT();
  const [form, setForm] = useState(EMPTY_DEAL);
  const [companies, setCompanies] = useState<CrmCompany[]>([]);
  const [contacts, setContacts] = useState<CrmContact[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(
        initial
          ? {
              title: initial.title,
              type: initial.type,
              currency: initial.currency,
              payment_terms: initial.payment_terms || '',
              shipping_terms: initial.shipping_terms || '',
              notes: initial.notes || '',
              company_id: String(initial.company_id || ''),
              contact_id: String(initial.contact_id || ''),
              expected_close_date: initial.expected_close_date || '',
            }
          : {
              ...EMPTY_DEAL,
              company_id: String(defaultCompanyId || ''),
              contact_id: String(defaultContactId || ''),
            },
      );
      fetchCompanies({ limit: 200 })
        .then((r) => setCompanies(r.items))
        .catch(() => {});
      fetchContacts({ limit: 200 })
        .then((r) => setContacts(r.items))
        .catch(() => {});
    }
  }, [open, initial, defaultCompanyId, defaultContactId]);

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast('Title is required', 'error');
      return;
    }
    setSaving(true);
    try {
      const data = {
        ...form,
        company_id: form.company_id ? Number(form.company_id) : null,
        contact_id: form.contact_id ? Number(form.contact_id) : null,
      };
      if (initial) {
        await updateDeal(initial.id, data);
        toast('Deal updated', 'success');
      } else {
        await createDeal(data);
        toast('Deal created', 'success');
      }
      onSaved();
      onClose();
    } catch (err: unknown) {
      toast((err as Error).message || t('crm.saveFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const set = (k: string, v: string) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <Dialog open={open} onClose={onClose} title={initial ? 'Edit Deal' : 'New Deal'} size="lg">
      <div className="space-y-3">
        <div>
          <label className="text-xs text-fg-muted mb-1 block">Title *</label>
          <Input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="e.g. Q3 Bulk Order" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-fg-muted mb-1 block">Type</label>
            <Select value={form.type} onChange={(e) => set('type', e.target.value)}>
              <option value="quotation">Quotation</option>
              <option value="sales_order">Sales Order</option>
            </Select>
          </div>
          <div>
            <label className="text-xs text-fg-muted mb-1 block">Currency</label>
            <Select value={form.currency} onChange={(e) => set('currency', e.target.value)}>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="CNY">CNY</option>
              <option value="JPY">JPY</option>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-fg-muted mb-1 block">Company</label>
            <Select value={form.company_id} onChange={(e) => set('company_id', e.target.value)}>
              <option value="">— None —</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="text-xs text-fg-muted mb-1 block">Contact</label>
            <Select value={form.contact_id} onChange={(e) => set('contact_id', e.target.value)}>
              <option value="">— None —</option>
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <div>
          <label className="text-xs text-fg-muted mb-1 block">Expected Close Date</label>
          <Input
            type="date"
            value={form.expected_close_date}
            onChange={(e) => set('expected_close_date', e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-fg-muted mb-1 block">Payment Terms</label>
            <Input
              value={form.payment_terms}
              onChange={(e) => set('payment_terms', e.target.value)}
              placeholder="e.g. Net 30"
            />
          </div>
          <div>
            <label className="text-xs text-fg-muted mb-1 block">Shipping Terms</label>
            <Input
              value={form.shipping_terms}
              onChange={(e) => set('shipping_terms', e.target.value)}
              placeholder="e.g. FOB Shenzhen"
            />
          </div>
        </div>
        <div>
          <label className="text-xs text-fg-muted mb-1 block">Notes</label>
          <Textarea
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
            rows={2}
            placeholder={t('crm.notesPlaceholder')}
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Spinner className="h-3 w-3 mr-1" />}
            {initial ? 'Save' : 'Create'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ─── Followup Form ───────────────────────────────────────

interface FollowupFormProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  companyId: number;
  contacts?: CrmContact[];
}

const EMPTY_FOLLOWUP = {
  contact_id: '' as string,
  purpose: '',
  result: '',
  outcome: '',
  next_followup_at: '',
  next_goal: '',
};

export function FollowupFormDialog({ open, onClose, onSaved, companyId, contacts = [] }: FollowupFormProps) {
  const t = useT();
  const [form, setForm] = useState(EMPTY_FOLLOWUP);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(EMPTY_FOLLOWUP);
  }, [open]);

  const set = (k: string, v: string) => setForm((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await createFollowup({
        company_id: companyId,
        contact_id: form.contact_id ? Number(form.contact_id) : undefined,
        purpose: form.purpose || undefined,
        result: form.result || undefined,
        outcome: form.outcome || undefined,
        next_followup_at: form.next_followup_at ? new Date(form.next_followup_at).toISOString() : undefined,
        next_goal: form.next_goal || undefined,
      });
      toast(t('crm.followupCreated'), 'success');
      onSaved();
      onClose();
    } catch (err: unknown) {
      toast((err as Error).message || t('crm.saveFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title={t('crm.logFollowup')} size="lg">
      <div className="space-y-3">
        {contacts.length > 0 && (
          <div>
            <label className="text-xs text-fg-muted mb-1 block">{t('crm.contact')}</label>
            <Select value={form.contact_id} onChange={(e) => set('contact_id', e.target.value)}>
              <option value="">—</option>
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </div>
        )}
        <div>
          <label className="text-xs text-fg-muted mb-1 block">{t('crm.purpose')}</label>
          <Input
            value={form.purpose}
            onChange={(e) => set('purpose', e.target.value)}
            placeholder={t('crm.purposePlaceholder')}
          />
        </div>
        <div>
          <label className="text-xs text-fg-muted mb-1 block">{t('crm.result')}</label>
          <Textarea
            value={form.result}
            onChange={(e) => set('result', e.target.value)}
            rows={3}
            placeholder={t('crm.resultPlaceholder')}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-fg-muted mb-1 block">{t('crm.outcome')}</label>
            <Input value={form.outcome} onChange={(e) => set('outcome', e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-fg-muted mb-1 block">{t('crm.nextFollowup')}</label>
            <Input
              type="date"
              value={form.next_followup_at}
              onChange={(e) => set('next_followup_at', e.target.value)}
            />
          </div>
        </div>
        <div>
          <label className="text-xs text-fg-muted mb-1 block">{t('crm.nextGoal')}</label>
          <Input
            value={form.next_goal}
            onChange={(e) => set('next_goal', e.target.value)}
            placeholder={t('crm.nextGoalPlaceholder')}
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Spinner className="h-3 w-3 mr-1" />}
            Create
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
